/**
 * Small persistent store for the local player's preferences and local cosmetic unlocks.
 */
interface PlayerPreferences {
    /** Local display preference only; absent means the default 3D rendering. */
    fun getDice2DMode(): Boolean
    fun setDice2DMode(enabled: Boolean)
    /** Local display preference: seasonal decoration (leaves, bats, fireworks). Absent means on. */
    fun getSeasonalEffectsEnabled(): Boolean = true
    fun setSeasonalEffectsEnabled(enabled: Boolean) {}
    fun getPlayerName(): String?
    fun setPlayerName(name: String)
    fun getDynamicColorEnabled(): Boolean
    fun setDynamicColorEnabled(enabled: Boolean)
    /** The local player's chosen dice skin id (see DiceSkin), applied to games they create/join. */
    fun getSelectedDiceSkin(): String?
    fun setSelectedDiceSkin(id: String)
    /** The local player's chosen dice hat id (see DiceHat) — the on-top cosmetic layer. */
    fun getSelectedDiceHat(): String?
    fun setSelectedDiceHat(id: String)
    /** Whether the achievement-gated "Lucky" dice has been unlocked (persisted so it survives offline). */
    fun getAchievementDiceUnlocked(): Boolean
    fun setAchievementDiceUnlocked(unlocked: Boolean)
    /** Whether the creator encounter dice has been unlocked locally. */
    fun getCreatorDiceUnlocked(): Boolean
    fun setCreatorDiceUnlocked(unlocked: Boolean)
    /** Whether the Training Wheels dice (finish a practice round) has been unlocked. */
    fun getPracticeDiceUnlocked(): Boolean
    fun setPracticeDiceUnlocked(unlocked: Boolean)
    /** Limited-time dice skin ids earned during their event windows. Current Plus still gates use. */
    fun getLimitedTimeDiceSkinUnlocks(): Set<String>
    fun setLimitedTimeDiceSkinUnlocks(ids: Set<String>)
    /** Last verified local ad-removal entitlement; used only as a cold-start hint before RevenueCat answers. */
    fun getAdsRemovedCached(): Boolean
    fun setAdsRemovedCached(removed: Boolean)
    /** Whether this player has been in a game (or dismissed the offer); gates the one-time practice-game card. */
    fun getHasCompletedFirstGame(): Boolean
    fun setHasCompletedFirstGame(completed: Boolean)
    /** Whether an unspent rewarded-ad Plus Pass is waiting to apply to the next created game. */
    fun getPlusPassPending(): Boolean
    fun setPlusPassPending(pending: Boolean)
    /** Rewarded-ad one-game dice trial: the skin/hat id waiting to apply to the next game (null = none). */
    fun getDiceTrialSkinId(): String?
    fun setDiceTrialSkinId(id: String?)
    fun getDiceTrialHatId(): String?
    fun setDiceTrialHatId(id: String?)
    /** Whether the one-time first-game banking hint has already been shown. */
    fun getBankingHintShown(): Boolean
    fun setBankingHintShown(shown: Boolean)
    /** The local player's lifetime stats as one JSON value (see models.CareerStatsCodec); null = none yet. */
    fun getCareerStatsJson(): String?
    fun setCareerStatsJson(json: String?)
}

internal fun decodeLimitedTimeDiceSkinUnlocks(value: String?): Set<String> =
    value
        ?.split(',')
        ?.map { it.trim() }
        ?.filter { it.isNotEmpty() }
        ?.toSet()
        .orEmpty()

internal fun encodeLimitedTimeDiceSkinUnlocks(ids: Set<String>): String =
    ids
        .asSequence()
        .map { it.trim() }
        .filter { it.isNotEmpty() }
        .distinct()
        .sorted()
        .joinToString(separator = ",")
